import React, { useState, useEffect } from "react";
import server from '../../../../api/server'
import { useToast } from '../../../../context/ToastContext'
import ModernTimeDropdown from "../../../../dashboard-components/layouts/modernDropdown";
import PhoneNumberActionConfirmModal from "./PhoneNumberActionConfirmModal";
import IvrRoutingConfig from "./IvrRoutingConfig";
import { createEmptyIvrConfig, normalizeIvrConfig, validateIvrConfig } from "./ivrConfiguration";

const PHONE_NUMBER_CHANGE_FREQUENCY_OPTIONS = Array.from({ length: 31 }, (_, i) => String(i));
const ROUTING_TYPE_OPTIONS = ['Direct to one agent', 'IVR menu'];

const isFrequencySet = (value) =>
  value !== null && value !== undefined && value !== '';

const formatApiError = (error) => {
  if (typeof error === 'string') return error
  if (!error || typeof error !== 'object') return 'Unable to update phone number'
  return Object.entries(error)
    .map(([field, message]) => `${field}: ${typeof message === 'string' ? message : JSON.stringify(message)}`)
    .join(' ')
}

const CreatePhoneNumberModal = ({selectedPhoneNumber, selectedAliasName, onCreatePhoneNumber, onClose, clientID, serviceID, email, role}) => {
  const [agent, setAgent] = useState('')
  const [provider, setProvider] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [aliasName, setAliasName] = useState('')
  const [managedBy, setManagedBy] = useState('')
  const [phoneNumberChangeFrequency, setPhoneNumberChangeFrequency] = useState('')
  const [rejectIncomingCalls, setRejectIncomingCalls] = useState(false)
  const [routingMode, setRoutingMode] = useState('direct')
  const [ivrConfig, setIvrConfig] = useState(createEmptyIvrConfig())
  const [ivrErrors, setIvrErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [isGeneratingNumber, setIsGeneratingNumber] = useState(false)
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false)
  const [agentsList, setAgentsList] = useState([])
  const { showToast } = useToast()
  const showAdminPhoneControls =
    role === 'superadmin' &&
    managedBy?.toLowerCase() === 'anthropod' &&
    provider?.toLowerCase() === 'plivo'

  const frequencyEnabled = isFrequencySet(phoneNumberChangeFrequency)
  const isPlivo = provider?.toLowerCase() === 'plivo'
  const showIvrRouting = isPlivo && !rejectIncomingCalls

  const handleFrequencyEnabledChange = (enabled) => {
    if (enabled) {
      setPhoneNumberChangeFrequency(PHONE_NUMBER_CHANGE_FREQUENCY_OPTIONS[0])
    } else {
      setPhoneNumberChangeFrequency('')
    }
  }

  const handleGenerateNewNumber = async () => {
    if (!showAdminPhoneControls) return
    try {
      setIsGeneratingNumber(true)
      const res = await server.generate_voice_agent_phone_number(
        clientID,
        serviceID,
        email,
        'phone-numbers',
        undefined,
        selectedPhoneNumber?.phone_number || phoneNumber,
        provider,
        managedBy
      )

      if (res.status !== 200) {
        showToast('Unable to generate new phone number', 'error')
        return
      }

      const resp = await res.json()
      const generatedPhoneNumber =
        resp?.data?.phone_number ||
        resp?.data?.new_phone_number ||
        resp?.phone_number ||
        resp?.new_phone_number

      if (!generatedPhoneNumber) {
        showToast('No phone number returned from server', 'error')
        return
      }

      showToast('New phone number generated successfully', 'success')
      onCreatePhoneNumber()
    } catch (error) {
      console.log(error)
      showToast('Unable to generate new phone number', 'error')
    } finally {
      setIsGeneratingNumber(false)
    }
  }
  const handleAdd = async () => {
    try {
        setLoading(true)
        if(!phoneNumber || !provider || !aliasName){
            setLoading(false)
            showToast('Please fill all the fields', 'error')
            return
        }
        const frequencyPayload =
          showAdminPhoneControls && frequencyEnabled
            ? Number(phoneNumberChangeFrequency)
            : undefined
        if (showIvrRouting && routingMode === 'ivr') {
          const ivrErrors = validateIvrConfig(ivrConfig)
          if (Object.keys(ivrErrors).length) {
            setIvrErrors(ivrErrors)
            setLoading(false)
            showToast('Please complete the IVR menu configuration', 'error')
            return
          }
          setIvrErrors({})
        }
        let res = await server.update_voice_agent_phone_number(
          clientID,
          serviceID,
          email,
          'phone-numbers',
          undefined,
          phoneNumber,
          agent,
          provider,
          aliasName,
          rejectIncomingCalls,
          frequencyPayload,
          {
            routing_mode: showIvrRouting ? routingMode : 'direct',
            ivr_config: showIvrRouting && routingMode === 'ivr' ? ivrConfig : {},
          }
        )
        if(res.status === 200){
            setLoading(false)
            setPhoneNumber('')
            setAgent('')
            setProvider('')
            setAliasName('')
            setRejectIncomingCalls(false)
            setRoutingMode('direct')
            setIvrConfig(createEmptyIvrConfig())
            onCreatePhoneNumber()
        } else {
            let errorMessage = 'Unable to update phone number'
            try {
              const errorBody = await res.json()
              errorMessage = formatApiError(errorBody?.error)
            } catch (error) {
              // Keep the generic message when the API does not return JSON.
            }
            showToast(errorMessage, 'error')
        }
    } catch (error) {
      console.log(error)
      setLoading(false)
      showToast('Unable to update phone number', 'error')
    }
  }

  const getAgentsList = async () => {
    let res = await server.getAgentsList(clientID, serviceID, email, 'phone-numbers', undefined)
    if(res.status === 200){
      let resp = await res.json()
      let agents = []
      for(let i=0;i<resp.data.length;i++){
        agents.push(resp.data[i].agent_id)
      }
      setAgentsList(agents)
    }
  }

  useEffect(()=>{
    getAgentsList()
    if(selectedPhoneNumber){
      setPhoneNumber(selectedPhoneNumber.phone_number)
      setAliasName(selectedPhoneNumber.alias_name)
      setAgent(selectedPhoneNumber.agent_id)
      setProvider(selectedPhoneNumber.provider)
      setRoutingMode(selectedPhoneNumber.routing_mode || 'direct')
      setIvrConfig(normalizeIvrConfig(selectedPhoneNumber.ivr_config))
      setIvrErrors({})
      setManagedBy(selectedPhoneNumber.managed_by || '')
      setPhoneNumberChangeFrequency(
        isFrequencySet(selectedPhoneNumber.phone_number_change_frequency)
          ? String(selectedPhoneNumber.phone_number_change_frequency)
          : ''
      )
      setRejectIncomingCalls(selectedPhoneNumber.reject_incoming_calls !== undefined ? selectedPhoneNumber.reject_incoming_calls : false)
    }
  }, [selectedPhoneNumber])

  const handleClose = () => {
    setPhoneNumber('')
    setAliasName('')
    setAgent('')
    setProvider('')
    setManagedBy('')
    setPhoneNumberChangeFrequency('')
    setRejectIncomingCalls(false)
    setRoutingMode('direct')
    setIvrConfig(createEmptyIvrConfig())
    setIvrErrors({})
    onClose()
  }
  return (
    <div className="pb-4 px-4">
      {!loading && <div style={{gap: "10px", display: "flex", flexDirection: "column", margin: "10px", minHeight: "77vh"}}>
        {showAdminPhoneControls ? (
        <div style={{ padding: "12px", borderRadius: "8px", border: "1px solid #e5e7eb", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: "8px" }}>
            <div style={{fontSize: "13px", marginBottom: "8px"}}>Phone Number</div>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <input
                value={phoneNumber}
                disabled={true}
                className=" px-3 py-2 border border-gray-200 rounded-lg focus:border-indigo-500"
                style={{fontSize: "13px", padding: "0.3rem"}}
              />
              <button
                className="flex items-center px-3 py-2 rounded text-xs text-white hover:opacity-90"
                style={{ backgroundColor: "rgb(79, 70, 229)", minWidth: "150px", justifyContent: "center" }}
                onClick={() => setShowGenerateConfirm(true)}
                disabled={isGeneratingNumber}
              >
                {isGeneratingNumber ? 'Generating...' : 'Generate New Number'}
              </button>
            </div>
            <div style={{ marginTop: "14px" }}>
              <label className="flex items-center space-x-2" style={{ marginBottom: "10px" }}>
                <input
                  type="checkbox"
                  checked={frequencyEnabled}
                  onChange={(e) => handleFrequencyEnabledChange(e.target.checked)}
                />
                <span style={{ fontSize: "13px" }}>Enable phone number change frequency</span>
              </label>
              {frequencyEnabled && (
                <>
                  <div style={{ fontSize: "13px", marginBottom: "6px" }}>Phone Number Change Frequency (days)</div>
                  <ModernTimeDropdown
                    options={PHONE_NUMBER_CHANGE_FREQUENCY_OPTIONS}
                    value={phoneNumberChangeFrequency}
                    placeholder="Select days"
                    width="w-56"
                    fontSize="13px"
                    onChange={(e) => setPhoneNumberChangeFrequency(e.target.value)}
                  />
                </>
              )}
            </div>
        </div>
        ) : (
        <div>
            <div style={{fontSize: "13px"}}>Phone Number</div>
            <input value={phoneNumber} disabled={true} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-indigo-500" style={{fontSize: "13px", padding: "0.3rem"}} />
        </div>
        )}
        <div>
            <div style={{fontSize: "13px"}}>Provider</div>
            <input value={provider} onChange={(e)=>setProvider(e.target.value)} disabled={true} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-indigo-500" style={{fontSize: "13px", padding: "0.3rem"}} />
        </div>
        <div>
            <div style={{fontSize: "13px"}}>Alias Name</div>
            <input value={aliasName} onChange={(e)=>setAliasName(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-indigo-500" style={{fontSize: "13px", padding: "0.3rem"}} />
        </div>
        <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={rejectIncomingCalls}
                onChange={(e)=>setRejectIncomingCalls(e.target.checked)}
              />
              <span style={{fontSize: "13px"}}>Reject Incoming Calls</span>
            </label>
        </div>
        {!rejectIncomingCalls && (
          <>
            {isPlivo && (
              <div>
                <div style={{fontSize: "13px"}}>Routing Type</div>
                <ModernTimeDropdown
                  options={ROUTING_TYPE_OPTIONS}
                  value={routingMode === 'ivr' ? ROUTING_TYPE_OPTIONS[1] : ROUTING_TYPE_OPTIONS[0]}
                  width="w-full"
                  fontSize="13px"
                  onChange={(e) => setRoutingMode(e.target.value === ROUTING_TYPE_OPTIONS[1] ? 'ivr' : 'direct')}
                />
              </div>
            )}
            {showIvrRouting && routingMode === 'ivr' ? (
              <IvrRoutingConfig
                value={ivrConfig}
                agents={agentsList}
                errors={ivrErrors}
                onChange={(nextConfig) => {
                  setIvrConfig(nextConfig)
                  setIvrErrors({})
                }}
              />
            ) : (
              <div>
                <div style={{fontSize: "13px"}}>Select Agent</div>
                <ModernTimeDropdown options={agentsList} value={agent} placeholder="Select Agent" width="w-full" fontSize="13px" onChange={(e)=>setAgent(e.target.value)} />
              </div>
            )}
          </>
        )}
      </div>}
      {
        loading && (
          <div className="flex justify-center items-center" style={{ flexDirection: "column", height: "77vh" }}>
              <div className="loader"></div>
              <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
          </div>
        )
      }
      <PhoneNumberActionConfirmModal
        isOpen={showGenerateConfirm}
        onClose={() => setShowGenerateConfirm(false)}
        onConfirm={() => {
          setShowGenerateConfirm(false)
          handleGenerateNewNumber()
        }}
      />
      <hr/>
      <div className="flex justify-end my-3">
        <button className="flex items-center px-4 py-2 rounded text-sm border border-gray-300 hover:opacity-90" style={{ marginRight: "10px" }} onClick={handleClose}>Cancel</button>
        <button className="flex items-center px-4 py-2 rounded text-sm text-white hover:opacity-90" style={{ backgroundColor: "rgb(79, 70, 229)" }} onClick={handleAdd}>Save</button>
      </div>
    </div>
  )
}

export default CreatePhoneNumberModal;
